import Geolocation from "@react-native-community/geolocation";
import { getQuery } from "../api";

/**
 * Retrieves the current geographic location of the device.
 *
 * @returns {Promise<{latitude: number, longitude: number}>} A promise that resolves with the latitude and longitude of the current position.
 */
const getCurrentLocation = (signal) => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return resolve(null);

    Geolocation.getCurrentPosition(
      (position) => {
        if (signal?.aborted) return resolve(null);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        if (signal?.aborted) return resolve(null);
        reject(error);
      }
    );
  });
};

/**
 * Fetches address suggestions from an API and formats them with capitalized words.
 *
 * @async
 * @param {string} address - The address string to search for suggestions.
 * @returns {Promise<string[]>} A promise that resolves to an array of formatted address strings.
 */
export const getAddress = async (address) => {
  try {
    const data = await getQuery(`${process.env.REACT_APP_PROPERTY_URL}?address=${encodeURIComponent(address)}`);

    return (data.addresses || []).map((addr) =>
      addr
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches property information from an API and retrieves the latitude and longitude.
 *
 * @async
 * @param {string} address - The address string to search for property information.
 * @returns {Promise<Object>} A promise that resolves to an object containing property information and latitude/longitude.
 */
export const getPropertyInfo = async (address) => {
  try {
    const propertyInfo = await getQuery(
      `${process.env.REACT_APP_PROPERTY_INFO_URL}?address=${encodeURIComponent(address)}`
    );

    const latlng = {
      lat: propertyInfo.propertyInfo.latitude,
      lng: propertyInfo.propertyInfo.longitude,
    };

    return {
      ...propertyInfo,
      propertyInfo: {
        ...propertyInfo.propertyInfo,
        latLng: latlng,
      },
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Gets the address corresponding to the current geographic coordinates.
 *
 * @async
 * @returns {Promise<string>} A promise that resolves with the address as a string, or an error message if fetching fails.
 */
export const getAddressFromCoordinates = () => {
  let cancelled = false;

  const promise = (async () => {
    try {
      const location = await getCurrentLocation();
      if (cancelled || !location) return null;

      const { latitude, longitude } = location;

      const addressAPI = `${process.env.REACT_APP_GETADDRESS_URL}?latitude=${latitude}&longitude=${longitude}`;
      const address = await getQuery(addressAPI);

      if (cancelled) return null;
      return { success: true, data: address };
    } catch (error) {
      if (cancelled) return null;
      return { success: false, error };
    }
  })();

  return {
    promise,
    cancel: () => {
      cancelled = true;
    },
  };
};

/**
 * Fetches property boundary information from a mapping service and converts it to coordinate polygons.
 * This function retrieves the geographic boundaries of a property for map visualization purposes.
 *
 * @async
 * @param {string} address - The property address to get boundaries for (e.g., "123 Main St, Virginia Beach, VA 23456").
 * @returns {Promise<Array<Array<{latitude: number, longitude: number}>>>} A promise that resolves to an array of polygon arrays.
 *   Each polygon is an array of coordinate objects with latitude and longitude properties.
 *   Multiple polygons may be returned for complex property shapes.
 * @throws {Error} Throws an error if the API request fails or if the response doesn't contain expected geometry data.
 */
export const getPropertyInfoMapBoundaries = async (address) => {
  try {
    const request = `${process.env.REACT_APP_PROPERTY_INFO_MAP_BOUNDARY_URL}?address=${encodeURIComponent(address)}`;
    const polygons = await getQuery(request);

    return polygons;
  } catch (error) {
    return {};
  }
};

/**
 * Fetches nearby places of a given type for a property's location.
 * Response key varies by type: `nearbyFireStations`, `nearbyPoliceStations`,
 * `nearbyParks`, `nearbyLibraries`, `nearbySchools` (object keyed by level), or `votingInfo`.
 *
 * @async
 * @param {('fire'|'police'|'parks'|'libraries'|'schools'|'voting')} type - Category of nearby places to fetch.
 * @param {Object} propertyInfo - Property data used to build the query.
 * @param {number} propertyInfo.latitude - Latitude of the property.
 * @param {number} propertyInfo.longitude - Longitude of the property.
 * @param {string} [propertyInfo.votingPrecinct] - Voting precinct identifier, required when type is `'voting'`.
 * @returns {Promise<Object>} Raw API response — extract the relevant key based on the requested type.
 * @throws {Error} Throws if the API request fails.
 */
export const getNearbyPlaces = async (type, propertyInfo) => {
  let url = `${process.env.REACT_APP_API_URL}/nearbyPlaces?type=${type}&latitude=${propertyInfo.latitude}&longitude=${propertyInfo.longitude}&votingPrecinct=${propertyInfo.votingPrecinct}`;
  try {
    const data = await getQuery(url);
    return data;
  } catch (error) {
    throw error;
  }
};
